import moment from 'moment';
import { useMemo, useState } from 'react';
import { AvailabilityStaff } from 'services/caller-id';
import aptActions from 'store/appt/actions';
import { aptUIActions } from 'store/appt/reducers';
import aptSelectors from 'store/appt/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useDayAvailability } from './use-day-availability';

// Staff × time grid bounds (design: 30-min slots, 9:00 AM → 9:00 PM)
export const DAY_START_MIN = 540;
export const DAY_END_MIN = 1260;
export const SLOT_STEP_MIN = 30;
export const SLOTS: number[] = Array.from(
  { length: (DAY_END_MIN - DAY_START_MIN) / SLOT_STEP_MIN + 1 },
  (_, i) => DAY_START_MIN + i * SLOT_STEP_MIN,
);

export const ANYONE_COL = 'anyone';

export const minutesToLabel = (m: number): string => {
  const h24 = Math.floor(m / 60);
  const min = m % 60;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  let h = h24 % 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')} ${ampm}`;
};

export const formatDuration = (min: number): string => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
};

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface DateOption {
  day: string;
  dow: string;
  full: string;
  iso: string; // YYYY-MM-DD
  isToday: boolean;
}

const toDateOption = (dt: moment.Moment): DateOption => {
  const isToday = dt.isSame(moment(), 'day');
  return {
    day: dt.format('D'),
    dow: isToday ? 'Today' : DOW_SHORT[dt.day()],
    full: dt.format('dddd, MMMM D'),
    iso: dt.format('YYYY-MM-DD'),
    isToday,
  };
};

// 7-day window starting today + weekOffset*7; window can't scroll before today
export const getDateOptions = (weekOffset: number): DateOption[] =>
  Array.from({ length: 7 }, (_, i) =>
    toDateOption(moment().startOf('day').add(weekOffset * 7 + i, 'day')));

export interface SelectedTime {
  minutes: number;
  label: string;
}

export interface UseBookingFormResult {
  weekOffset: number;
  dateOptions: DateOption[];
  selectedDate: DateOption;
  pickDate: (option: DateOption) => void;
  prevWeek: () => void;
  nextWeek: () => void;
  staff: AvailabilityStaff[];
  isSlotFree: (colId: string, slot: number) => boolean;
  busyType: (staffId: string, slot: number) => 'appt' | 'block' | undefined;
  selectedStaff: string | null;
  selectedTime: SelectedTime | null;
  durationMin: number;
  isSlotSelected: (colId: string, slot: number) => boolean;
  tapCell: (colId: string, slot: number) => void;
  // selected quick-service ids (GET service/quickBooking)
  services: number[];
  toggleService: (id: number) => void;
  removeService: (id: number) => void;
  // Re book: seed services + duration from a past visit
  applyRebook: (serviceIds: number[], durationMin: number) => void;
  note: string;
  setNote: (v: string) => void;
  serviceSheetOpen: boolean;
  setServiceSheetOpen: (v: boolean) => void;
  // full reset when the form opens for a new call (prototype openForm behavior)
  reset: () => void;
}

// Form state for the State-3 booking page. Lives in CallerIdOverlay (not the
// page) so the ghost slot stays visible after Back; reset() on each openForm.
// The date selection drives the calendar's own store date (same as the
// calendar's date picker), so availability derives from the already-loaded
// appointments/block hours — no separate availability API.
export const useBookingForm = (): UseBookingFormResult => {
  const dispatch = useAppDispatch();
  const calendarDateStr = useAppSelector(aptSelectors.date);
  const availability = useDayAvailability();

  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<SelectedTime | null>(null);
  const [durationMin, setDurationMin] = useState(30);
  const [pickingEnd, setPickingEnd] = useState(false);
  const [services, setServices] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [serviceSheetOpen, setServiceSheetOpen] = useState(false);

  const dateOptions = useMemo(() => getDateOptions(weekOffset), [weekOffset]);
  // single source of truth: the calendar's displayed date IS the form's date
  const selectedDate = useMemo(() => toDateOption(moment(calendarDateStr)), [calendarDateStr]);

  const resetSelection = () => {
    setSelectedStaff(null);
    setSelectedTime(null);
    setDurationMin(30);
    setPickingEnd(false);
  };

  const setCalendarDate = (iso: string) => {
    dispatch(aptUIActions.setDate(moment(iso)));
    dispatch(aptActions.getAptData.fetch(`start_time=${iso}&end_time=${iso}`));
  };

  const pickDate = (option: DateOption) => {
    if (option.iso !== selectedDate.iso) setCalendarDate(option.iso);
    resetSelection();
  };
  const prevWeek = () => {
    if (weekOffset === 0) return;
    setWeekOffset(weekOffset - 1);
  };
  const nextWeek = () => setWeekOffset(weekOffset + 1);

  const staff = availability.staff;
  const busyType = (staffId: string, slot: number) => availability.busy[staffId]?.[slot];
  // "Anyone" column is free iff any staff is free at that slot
  const isSlotFree = (colId: string, slot: number): boolean => {
    if (staff.length === 0) return false;
    if (colId === ANYONE_COL) return staff.some(s => !busyType(s.id, slot));
    return !busyType(colId, slot);
  };

  const isSlotSelected = (colId: string, slot: number): boolean =>
    selectedStaff === colId && selectedTime !== null &&
    slot >= selectedTime.minutes && slot < selectedTime.minutes + durationMin;

  // Two-tap range selection: tap 1 = staff + start (30m); tap 2 in the SAME
  // column = duration between taps + 30m; a different column restarts as tap 1.
  // A range is only valid when every slot between the taps is free too.
  const rangeIsFree = (colId: string, from: number, to: number): boolean => {
    for (let s = from; s <= to; s += SLOT_STEP_MIN) {
      if (!isSlotFree(colId, s)) return false;
    }
    return true;
  };

  const tapCell = (colId: string, slot: number) => {
    if (!isSlotFree(colId, slot)) return;
    if (pickingEnd && selectedStaff === colId && selectedTime && slot > selectedTime.minutes
      && rangeIsFree(colId, selectedTime.minutes, slot)) {
      setDurationMin(slot - selectedTime.minutes + SLOT_STEP_MIN);
      setPickingEnd(false);
      return;
    }
    setSelectedStaff(colId);
    setSelectedTime({ minutes: slot, label: minutesToLabel(slot) });
    setDurationMin(SLOT_STEP_MIN);
    setPickingEnd(true);
  };

  const toggleService = (id: number) =>
    setServices(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  const removeService = (id: number) => setServices(prev => prev.filter(s => s !== id));
  const applyRebook = (serviceIds: number[], duration: number) => {
    setServices(serviceIds);
    setDurationMin(duration);
  };

  const reset = () => {
    setWeekOffset(0);
    resetSelection();
    setServices([]);
    setNote('');
    setServiceSheetOpen(false);
  };

  return {
    weekOffset, dateOptions, selectedDate, pickDate, prevWeek, nextWeek,
    staff, isSlotFree, busyType,
    selectedStaff, selectedTime, durationMin, isSlotSelected, tapCell,
    services, toggleService, removeService, applyRebook,
    note, setNote,
    serviceSheetOpen, setServiceSheetOpen,
    reset,
  };
};
